import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";


const SelectFacebookPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("sessionId");
  const userName = localStorage.getItem("UserName");
  const navigate = useNavigate();

  const [selectedPage, setSelectedPage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      fetchFacebookData();
    } else {
      setError("No session ID provided");
      setLoading(false);
    }
  }, [sessionId]);

  const fetchFacebookData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/facebook/page-selection-data/${sessionId}`
      );
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching Facebook data:", err);
      setError(err.response?.data?.error || "Failed to load page data");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedPage) return alert("Please select a page");
    if (!data) return alert("No data available");

    try {
      // Use the API endpoint to save the selected page
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/facebook/save-selected-page`,
        {
          sessionId,
          selectedPageId: selectedPage
        }
      );

      if (response.data.success) {
        // Find the selected page data
        const selectedPageData = data.pages.find((p) => p.id === selectedPage);
        
        if (selectedPageData) {
          // Update localStorage with selected page information (similar to LinkedIn pattern)
          const pageProfileData = [
            {
              UserID: data.UserID,
              FullName: selectedPageData.name,
              OrganizationName: selectedPageData.name,
              ProfileUrl: selectedPageData.picture || "",
              AccessToken: "", // Not needed in localStorage since it's handled server-side
              Tokenid: selectedPageData.id,
              AccountType: "Page",
              PlatformName: "Facebook",
              IsActive: true,
            },
          ];

          localStorage.setItem("userProfile", JSON.stringify(pageProfileData));
          localStorage.setItem("AccessToken", ""); // Will be fetched from server when needed
          localStorage.setItem("Tokenid", selectedPageData.id);
          localStorage.setItem("FullName", selectedPageData.name);
          localStorage.setItem("OrganizationName", selectedPageData.name);
          localStorage.setItem("ProfileUrl", selectedPageData.picture || "");
          localStorage.setItem("AccountType", "Page");
          
          console.log("✅ Facebook page saved to localStorage:", selectedPageData.name);
        }
        
        // Redirect to pagehome
        navigate("/pagehome");
      } else {
        const errorMsg = response.data.error || "Failed to save selected page. Please try again.";
        console.error("❌ Save failed:", response.data);
        alert(errorMsg);
      }
    } catch (err) {
      console.error("❌ Error saving selected page:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Error saving selected page. Please try again.";
      console.error("❌ Full error response:", err.response?.data);
      alert(`Error: ${errorMessage}\n\nPlease check the browser console and backend logs for more details.`);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f4f6fb",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", color: "#2e3192", marginBottom: "20px" }}>
            Loading Facebook pages...
          </div>
          <div style={{ fontSize: "14px", color: "#666" }}>
            Please wait while we fetch your available pages.
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f4f6fb",
        }}
      >
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "18px", color: "#e74c3c", marginBottom: "20px" }}>
            Error Loading Pages
          </div>
          <div style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
            {error}
          </div>
          <button
            onClick={() => window.location.href = "/pagehome"}
            style={{
              padding: "10px 20px",
              backgroundColor: "#2e3192",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.pages || data.pages.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f4f6fb",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", color: "#2e3192", marginBottom: "20px" }}>
            No Facebook Pages Found
          </div>
          <div style={{ fontSize: "14px", color: "#666" }}>
            No Facebook pages were found for your account.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",
        backgroundColor: "#f4f6fb",
        minHeight: "100vh",
        alignItems: "flex-start"
      }}
    >
      <div
        style={{
          padding: "40px",
          borderRadius: "20px",
          backgroundColor: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          width: "700px",
        }}
      >
        <h3
          style={{
            marginBottom: "30px",
            color: "#2e3192",
            textAlign: "center",
            fontSize: "22px",
          }}
        >
          Select a Facebook Page
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
          }}
        >
          {data.pages.map((page) => {
            const isSelected = selectedPage === page.id;
            const pictureUrl = page.picture || "https://via.placeholder.com/80";

            return (
              <div
                key={page.id}
                onClick={() => setSelectedPage(page.id)}
                style={{
                  position: "relative",
                  padding: "20px",
                  borderRadius: "15px",
                  border: isSelected ? "2px solid #2e3192" : "1px solid #ddd",
                  backgroundColor: isSelected ? "#eef2ff" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: isSelected
                    ? "0 8px 20px rgba(46,49,146,0.25)"
                    : "0 4px 12px rgba(0,0,0,0.08)",
                  textAlign: "center",
                  transform: isSelected ? "scale(1.05)" : "scale(1)",
                }}
              >
                {/* Tick mark overlay when selected */}
                {isSelected && (
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      backgroundColor: "#2e3192",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "22px",
                      height: "22px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓
                  </div>
                )}

                {/* Profile Picture */}
                <img
                  src={pictureUrl}
                  alt={page.name}
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    marginBottom: "12px",
                    border: isSelected ? "2px solid #2e3192" : "1px solid #ccc",
                  }}
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/80";
                  }}
                />

                {/* Name */}
                <div
                  style={{
                    fontWeight: "600",
                    color: isSelected ? "#2e3192" : "#333",
                    fontSize: "15px",
                  }}
                >
                  {page.name || `Page ${page.id}`}
                </div>

                {/* Category (if available) */}
                {page.category && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    {page.category}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: "center" }}>
          <button
            onClick={() => handleConfirm(selectedPage)}
            disabled={!selectedPage}
            style={{
              padding: "14px 36px",
              backgroundColor: selectedPage ? "#2e3192" : "#ccc",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              cursor: selectedPage ? "pointer" : "not-allowed",
              fontWeight: "bold",
              fontSize: "16px",
              boxShadow: selectedPage
                ? "0 6px 16px rgba(46,49,146,0.3)"
                : "none",
              transition: "all 0.3s ease",
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectFacebookPage;

